import { useCallback, useEffect, useState } from 'react';
import { api, APIError, type PortalEditorView } from '../../../lib/api';
import DealStatusForm from './DealStatusForm';
import TimelineEntryForm from './TimelineEntryForm';
import OpenItemsEditor from './OpenItemsEditor';
import EditHistory from './EditHistory';
import { QUIET, type SaveChange } from './editorShared';

/**
 * VP9 - "Update the portal": the everyday updates Joseph used to make through chat, made here instead
 * (spec docs/planning/2026-09-10-vp9-portal-editor-spec.md). A deal's status and where it stands, a dated timeline
 * entry, and open items added, edited and closed.
 *
 * The server decides everything that matters - the rules, the import's whole gate, the notes, the history - and this
 * screen asks it honestly: every save carries the version of the portal it was made on, a refusal prints every reason
 * word for word under the form that caused it, and a 409 (the portal changed since this view loaded) locks the forms
 * behind a Reload rather than letting a stale screen overwrite a newer change. There is no field for a figure: the
 * asks, caps, NOI, SF, price per SF and the five floors stay quoted from the documents.
 */
export default function PortalEditor({ portalId }: { portalId: string }) {
  const [view, setView] = useState<PortalEditorView | null>(null);
  const [loadError, setLoadError] = useState('');
  const [stale, setStale] = useState<string | null>(null);
  const [undoErrors, setUndoErrors] = useState<string[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await api.getPortalEditor(portalId);
      setView(res.editor);
      setLoadError('');
      setStale(null);
      setUndoErrors([]);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load the editor');
    }
  }, [portalId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** A save's 409 goes to the banner (the whole page is out of date); an undo's may be a refusal, so it stays inline. */
  const refusalOf = (e: unknown, conflict: 'banner' | 'inline'): string[] => {
    if (e instanceof APIError) {
      if (e.status === 409 && conflict === 'banner') {
        setStale(e.message);
        return [];
      }
      const v = (e.body as { violations?: unknown } | undefined)?.violations;
      return Array.isArray(v) && v.length ? v.map(String) : [e.message];
    }
    return [e instanceof Error ? e.message : 'The change could not be saved.'];
  };

  const save: SaveChange = async (body) => {
    if (!view) return ['The editor has not loaded yet.'];
    try {
      const res = await api.savePortalEdit(portalId, { ...body, expectedUpdatedAt: view.portal.updatedAt });
      setView(res.editor);
      return null;
    } catch (e) {
      return refusalOf(e, 'banner');
    }
  };

  const undo = async (seq: number) => {
    if (!view) return;
    setUndoErrors([]);
    try {
      const res = await api.undoPortalEdit(portalId, seq, view.portal.updatedAt);
      setView(res.editor);
    } catch (e) {
      setUndoErrors(refusalOf(e, 'inline'));
    }
  };

  const heading = <h2 className="text-sm font-semibold text-gray-900 mb-1">Update the portal</h2>;
  if (loadError) {
    return (
      <div>
        {heading}
        <p className="text-sm text-red-600">{loadError}</p>
      </div>
    );
  }
  if (!view) {
    return (
      <div>
        {heading}
        <p className="text-sm text-gray-500">Loading the editor...</p>
      </div>
    );
  }

  const locked = !view.consistent || stale !== null;
  return (
    <div data-testid="portal-editor">
      {heading}
      <p className="text-xs text-gray-500 mb-3 max-w-[900px]">
        Every change goes through the same checks as an import, is recorded below with who made it and when, and can be
        undone. Figures - asks, caps, NOI, SF, price per SF, the five floors - stay quoted from the documents and are
        never typed here.
      </p>
      {!view.consistent && (
        <div role="alert" className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          This portal was changed outside the editor since its last import - re-import it before editing.
        </div>
      )}
      {stale !== null && (
        <div role="alert" className="mb-3 p-3 rounded-lg bg-warning-wash border border-warning/30 text-sm text-gray-900 flex flex-wrap items-center gap-3">
          <span>{stale}</span>
          <button type="button" className={QUIET} onClick={() => void load()}>
            Reload
          </button>
        </div>
      )}
      <fieldset disabled={locked} className="space-y-6 min-w-0">
        {view.supports.status && <DealStatusForm view={view} onSave={save} />}
        {view.supports.timeline && <TimelineEntryForm view={view} onSave={save} />}
        {view.supports.openItems ? (
          <OpenItemsEditor view={view} onSave={save} />
        ) : (
          view.supports.openItemsWhy && <p className="text-sm text-gray-500">{view.supports.openItemsWhy}</p>
        )}
      </fieldset>
      <EditHistory view={view} onUndo={undo} errors={undoErrors} disabled={locked} onReload={() => void load()} />
    </div>
  );
}
